import { useWorkspaceId } from "../../workspace/useWorkspaceId";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "@tanstack/react-store";
import { useTranslation } from "react-i18next";
import { store } from "../../../store/store";
import type { PaginatedDTO } from "../../api/utils/PaginatedDTO";
import { getQueryStringFromParams } from "../../api/utils/paramUtils";
import {
    BASE_API_URL,
    HttpError,
} from "../../api/utils/useGovernmentAgencyHubAppQueryClient";
import { useHttpGovernmentAgencyHubAppHeaders } from "../../api/utils/useHttpGovernmentAgencyHubAppHeaders";
import type { TaxSubmissionDTO } from "./TaxSubmissionDTO";

export function useGetTaxSubmissions() {
    const workspaceId = useWorkspaceId();
    const headers = useHttpGovernmentAgencyHubAppHeaders();
    const { t } = useTranslation();
    const filters = useSelector(
        store,
        (state) => state.taxSubmissionsSearchFilters,
    );

    const fullTextSearch = filters.fullTextSearch
        ?.trim()
        .replaceAll(/\s{2,}/g, " ");

    const queryParams = {
        deadlineFrom: filters.deadlineFrom.getTime() || 0,
        deadlineTo: filters.deadlineTo.getTime() || 0,
        ...(filters.operator ? { operator: filters.operator } : {}),
        ...(filters.intermediary ? { intermediary: filters.intermediary } : {}),
        ...(filters.taxPayer ? { taxPayer: filters.taxPayer } : {}),
        ...(fullTextSearch ? { fullTextSearch: fullTextSearch } : {}),
        ...(filters.expired ? { expired: filters.expired } : {}),
        limit: filters.pageSize,
        skip: filters.page * filters.pageSize,
        ...(filters.statuses.length > 0 ? { statuses: filters.statuses } : {}),
        ...(filters.sort?.field ? { sortField: filters.sort?.field } : {}),
        ...(filters.sort?.order ? { sortOrder: filters.sort?.order } : {}),
    };
    const query = useQuery<PaginatedDTO<TaxSubmissionDTO>>({
        queryKey: ["useGetTaxSubmissions", workspaceId, filters],
        queryFn: async () => {
            const queryString = getQueryStringFromParams(queryParams);
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/tax-submissions?${queryString}`,
                {
                    headers,
                },
            );

            if (!response.ok) {
                throw new HttpError(response);
            }

            return response.json();
        },
        enabled: !!workspaceId,
        initialData: { data: [], totalCount: 0 },
        placeholderData: () => ({ data: [], totalCount: 0 }),
        meta: {
            resourceName: t("errors.resourceName.taxSubmissions"),
        },
    });

    return query;
}
